import { AbstractEntitiesTableComponent } from "@abstracts/entities/entities-table";
import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { TableColumn } from "@models/tableColumn";
import { ChargingStationsService } from "@services/entity/charging-stations.service";
import { UsersService } from "@services/entity/users.service";
import { plainToClass } from "class-transformer";
import { Subscription } from "rxjs";
import "@angular/localize/init";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ChargingStationTableDto } from "@dto/chargingStationTableDto";
import { TableOptions } from "@models/tableOptions";
import { DomSanitizer } from "@angular/platform-browser";
import { UpdateChargingStationsDialogContentComponent } from "../../../dialog/update-charging-station-dialog/update-charging-station-dialog-content/update-charging-stations-dialog-content";
import { ToastService } from "@services/toast/toast.service";
import { DEDatePipe } from "../../../../pipes/de-date.pipe";
import { ChargingPointStatusEnum } from "@enums/chargingPointStatus.enum";

@Component({
  selector: "app-charging-stations-table",
  templateUrl: "./charging-stations-table.component.html",
  styleUrls: ["./charging-stations-table.component.scss"],
})
export class ChargingStationsTableComponent
  extends AbstractEntitiesTableComponent
  implements OnInit, OnDestroy
{
  public className = "ChargingStationsTableComponent";

  @ViewChild("customerView") customerView: TemplateRef<any>;
  @ViewChild("userView") userView: TemplateRef<any>;
  @ViewChild("operatorView") operatorView: TemplateRef<any>;

  @Output() stationCount = new EventEmitter<number>();

  public entities: ChargingStationTableDto[];
  public isOperatorView: boolean;
  public user: boolean = false;
  public customer: boolean = false;
  public operator: boolean = false;
  public showAll: boolean = false;

  public isProduction: boolean = false;
  public isSupporter: boolean = false;
  private statusUpdateSubscription: Subscription;

  @Input() public stations: ChargingStationTableDto[];
  @Input() public userId: string;

  @Output() newEntityEvent = new EventEmitter<ChargingStationTableDto>();

  private sortParam: number;

  public tableColumns: TableColumn[] = [
    {
      columnDescription: $localize`:@@ChargingStationsTableColumn2:Name/Bezeichner`,
      property: "name",
    },
    {
      columnDescription: $localize`:@@ChargingStationsTableColumn6:Status`,
      property: "chargingPointsStatus",
      options: { useStatusBadgeComponent: true },
    },
    {
      columnDescription: $localize`:@@ChargingStationsTableColumn6:Lademodus`,
      property: "displayChargeMode",
      options: { isHTML: true },
    },
    {
      columnDescription: $localize`:@@ChargingStationsTableColumn4:Hersteller`,
      property: "manufacturer.name",
    },
    {
      columnDescription: $localize`:@@ChargingStationsTableColumn5:Modell`,
      property: "model.name",
    },
    {
      columnDescription: $localize`:@@ChargingStationsTableColumn6:erstellt am`,
      property: "installationDate",
      options: { columnPipe: { pipe: new DEDatePipe() } },
    },
  ];

  public tableOptions: TableOptions = {
    hideActions: true,
    rowRouterLink: "edit/{id}",
  };

  constructor(
    public chargingStationsService: ChargingStationsService,
    public usersService: UsersService,
    public toastService: ToastService,
    public sanitizer: DomSanitizer,
    private dialog: NgbModal,
    injector: Injector
  ) {
    super(chargingStationsService, injector);
  }

  ngOnInit(): void {
    this.getTableLayout("ChargingStationsTableComponent");
    this.getRoles();
    this.isOperatorView = this.activatedRoute.snapshot.data["isOperatorView"];
    this.showAll = this.activatedRoute.snapshot.data["showAll"];

    this.activatedRoute.queryParams.subscribe((params) => {
      this.sortParam = params.sort;
    });
    super.ngOnInit();

    if (!this.sortParam) {
      this.tableFunctions.sortColumn = "name";
      this.tableFunctions.sortDirection = "asc";
    }
    if (this.showAll) {
      //this.refreshSub.unsubscribe();
    }

    this.usersService.getUserByEmail("technik@cubos.com").subscribe(
      (user) => {
        if (this.usersService.getCurrentUserId() == user.id) {
          this.isProduction = true;
        } else {
          this.isProduction = false;
        }
      },
      (error) => {
        console.error("User konnte nicht abgerufen werden");
        this.isProduction = false;
      }
    );
  }

  getTableLayout(componentClassName: string) {
    if (localStorage.getItem(`${componentClassName}TableLayout`) == null) {
      localStorage.setItem(`${componentClassName}TableLayout`, "table-layout");
    }
    return true;
  }

  getRoles() {
    if (this.authenticationService.userHasRole("Customer")) {
      this.customer = true;
    }
    if (this.authenticationService.userHasRole("Operator")) {
      this.operator = true;
    }
    if (this.authenticationService.userHasRole("User")) {
      this.user = true;
    }
    this.isSupporter = this.authenticationService.userHasRole("Supporter");
  }

  async initializeEntities() {
    if (this.isOperatorView) {
      if (this.showAll) {
        this.chargingStationsService.getAllChargingStations().subscribe(
          (result) => {
            this.entities = plainToClass(ChargingStationTableDto, result);
            this.stationCount.emit(this.entities.length);
            this.subscribeStatusUpdateEvent();
            this.sortEntities();
            this.mapChargeMode();
            super.setupTable();
          },
          (error) => {}
        );
        this.tableOptions = { hideActions: true, rowRouterLink: "edit/{id}" };

        this.tableColumns = [
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn2:Name/Bezeichner`,
            property: "name",
          },
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn6:Status`,
            property: "chargingPointsStatus",
            options: { useStatusBadgeComponent: true },
          },
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn6:Lademodus`,
            property: "displayChargeMode",
            options: { isHTML: true },
          },
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn4:Hersteller`,
            property: "manufacturer.name",
          },
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn3:Modell`,
            property: "model.name",
          },
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn6:Firmware`,
            property: "firmware",
          },
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn6:Kunde`,
            property: "customer.name",
          },
          {
            columnDescription: $localize`:@@ChargingStationsTableColumn6:erstellt am`,
            property: "installationDate",
            options: { columnPipe: { pipe: new DEDatePipe() } },
          },
        ];
        //this.refreshSub.unsubscribe();
      } else {
        var customerId = this.activatedRoute.parent.snapshot.params.key;
        this.chargingStationsService
          .getChargingStationsOfCustomer(customerId)
          .subscribe(
            (result) => {
              this.entities = plainToClass(ChargingStationTableDto, result);
              this.stationCount.emit(this.entities.length);
              this.subscribeStatusUpdateEvent();
              this.sortEntities();
              this.mapChargeMode();
              super.setupTable();
            },
            (error) => {}
          );
      }
    } else if (this.authenticationService.userHasRole("User")) {
      this.tableColumns = [
        {
          columnDescription: $localize`:@@ChargingStationsTableColumn2:Name/Bezeichner`,
          property: "name",
        },
        {
          columnDescription: $localize`:@@ChargingStationsTableColumn6:Status`,
          property: "chargingPoints",
          options: { useStatusBadgeComponent: true },
        },
        {
          columnDescription: $localize`:@@ChargingStationsTableColumn4:Adresse`,
          property: "location",
        },
      ];

      this.chargingStationsService.getChargingStationsOfCurrentUser().subscribe(
        (result) => {
          this.entities = plainToClass(ChargingStationTableDto, result);
          this.stationCount.emit(this.entities.length);
          this.subscribeStatusUpdateEvent();
          this.sortEntities();
          this.mapChargeMode();
          super.setupTable();
        },
        (error) => {}
      );
    } else {
      this.chargingStationsService
        .getChargingStationsOfCurrentCustomer()
        .subscribe(
          (result) => {
            this.entities = plainToClass(ChargingStationTableDto, result);
            this.stationCount.emit(this.entities.length);
            this.subscribeStatusUpdateEvent();
            this.sortEntities();
            this.mapChargeMode();
            super.setupTable();
          },
          (error) => {}
        );
    }
  }

  sortEntities() {
    // pre-sort by status column if required
    if (this.sortParam) {
      // all that have sortParam in their status column:
      var entitiesWithSortParam = this.entities.filter(
        (x) =>
          x.statusId == this.sortParam ||
          x.chargingPoints.some((cp) => cp.statusId == this.sortParam)
      );
      // all others:
      var entitiesWithoutSortParam = this.entities.filter(
        (x) => !entitiesWithSortParam.includes(x)
      );
      this.entities = entitiesWithSortParam.concat(entitiesWithoutSortParam);
    }

    // create searchable string for the status column
    this.entities.forEach((e) => {
      // The charging station status badge shows each CP status as long as the CS is available and the CS status otherwise.
      if (e.status.id == ChargingPointStatusEnum.Available) {
        e.chargingPointsStatus = e.chargingPoints
          .map((cp) => cp.status.name)
          .join();
      } else {
        e.chargingPointsStatus = e.status.name;
      }
    });
  }

  openUpdateModalDialog() {
    const dialogRef = this.dialog.open(
      UpdateChargingStationsDialogContentComponent,
      { centered: true, size: "xl", backdrop: "static" }
    );
    dialogRef.componentInstance.chargingStations = this.entities;
    dialogRef.result.then(
      (result) => {},
      (backdrop) => {}
    );
  }

  mapChargeMode() {
    this.entities.forEach((f) => {
      let chargeMode = "";
      if (f.isFreeCharge) {
        chargeMode = `<span class="text-success">Kostenloses Laden</span>`;
      }
      if (f.isPrivateCharge) {
        chargeMode = `<span class="text-dark">Autorisiertes Laden</span>`;
      }
      if (f.isPublicCharge) {
        chargeMode = `<span class="text-orange">Autorisiertes &amp; QR-Code-Laden</span>`;
      }
      f.displayChargeMode = chargeMode;
    });
  }

  onSort() {
    if (this.sortParam) {
      this.sortParam = undefined;
      this.router.navigate([], {
        queryParams: {
          sort: null,
        },
        queryParamsHandling: "merge",
      });
    }
  }

  subscribeStatusUpdateEvent() {
    if (this.entities.length == 0) return;
    this.statusUpdateSubscription = this.chargingStationsService
      .subscribeStatusUpdateToStations(this.entities.map((cs) => cs.id))
      .subscribe(
        (event) => {
          const chargingStation = this.entities.find(
            (cs) => cs.id == event.chargingStationId
          );
          this.chargingStationsService.updateChargingPointsStatusThroughSSE(
            event,
            chargingStation
          );
        },
        (error) => {}
      );
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    if (!this.entities || this.entities.length != 0) {
      this.statusUpdateSubscription.unsubscribe();
      this.chargingStationsService.unsubscribeStatusUpdateToStations(
        this.entities.map((cs) => cs.id)
      );
    }
  }

  matches(entity: ChargingStationTableDto, term: string) {
    return ChargingStationsService.search(entity, term);
  }

  checkChargingStationStatus() {
    var customerId = this.activatedRoute.parent.snapshot.params.key;
    this.toastService.loading(
      "Erreichbarkeit der Ladestationen wird geprüft...",
      true
    );
    this.chargingStationsService.checkOnlineStatus(customerId).subscribe(
      (result) => {
        if (result) this.toastService.success("Prüfung abgeschlossen.");
      },
      (error) => {
        this.toastService.success(
          "Prüfung konnte nicht durchgeführt werden.",
          0,
          true
        );
      }
    );
  }

  PushEVSEData() {
    this.toastService.loading("EVSE Data wird sychronisiert...", true);
    this.chargingStationsService.pushEVSEData().subscribe(
      (ackonwledgement) => {
        if (ackonwledgement.result)
          this.toastService.success(
            "EVSEData erfolgreich sychronisiert",
            0,
            true
          );
        else {
          this.toastService.error(
            "EVSEData wurde nicht sychronisiert",
            0,
            true
          );
          console.error(ackonwledgement);
        }
      },
      (error) => {
        this.toastService.error("EVSEData wurde nicht sychronisiert", 0, true);
        console.error(error);
      }
    );
  }

  PushEVSEStatus() {
    this.toastService.loading("EVSE Status wird sychronisiert...", true);
    this.chargingStationsService.pushEVSEStatus().subscribe(
      (ackonwledgement) => {
        if (ackonwledgement.result)
          this.toastService.success(
            "EVSEStatus erfolgreich sychronisiert",
            0,
            true
          );
        else {
          this.toastService.error(
            "EVSEStatus wurde nicht sychronisiert",
            0,
            true
          );
          console.error(ackonwledgement);
        }
      },
      (error) => {
        this.toastService.error(
          "EVSEStatus wurde nicht sychronisiert",
          0,
          true
        );
        console.error(error);
      }
    );
  }

  determineTemplate() {
    if (this.showAll) {
      return this.operatorView;
    } else if (
      this.authenticationService.userHasRole("Customer") ||
      this.authenticationService.userHasRole("Operator")
    ) {
      return this.customerView;
    } else if (
      this.authenticationService.userHasRole("User") ||
      this.authenticationService.userHasRole("Account")
    ) {
      return this.userView;
    }
  }
}
